import { CheckBox, CheckBoxOutlineBlank, ExpandMore } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, SxProps, TextField, useTheme } from '@mui/material';
import React, { ReactNode } from 'react';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

// Minimum shape every option must satisfy — `name` for display, plus `id`
// or `tag` as the unique key. Concrete callers pass entity types like `IFund`,
// `IInvestor`, etc. that satisfy this structurally.
type WithKey = {
	name: string;
	id?: string | number | boolean | null;
	tag?: string | null;
	icon?: ReactNode;
	color?: string | null;
};

const optionKey = (o: WithKey | null | undefined): string | number | boolean | null | undefined => o?.id ?? o?.tag;

// Value/options/onChange/multipleRenderTags are typed as `unknown`-like to
// accept the wide variety of entity-shape options the codebase passes in.
// Internal accesses go through `WithKey` for the equality + render logic.
type CAppFilterSelectProps = {
	value?: WithKey | WithKey[] | null | undefined;
	onChange?: (val: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
	options: WithKey[] | null | undefined;
	multiple?: boolean;
	multipleRenderTags?: (val: any) => ReactNode; // eslint-disable-line @typescript-eslint/no-explicit-any
	className?: string;
	size?: 'small' | 'medium';
	disabled?: boolean;
	label?: ReactNode;
	required?: boolean;
	error?: boolean;
	helperText?: ReactNode;
	sx?: SxProps;
};

const CAppFilterSelect = (props: CAppFilterSelectProps) => {
	const { value, onChange, size, options, disabled, label, multiple, multipleRenderTags } = props;
	const theme = useTheme();

	const handleChange = (v: WithKey | WithKey[] | null) => {
		if (Array.isArray(v) && v.length > 0 && multiple) {
			// MUI returns the full selection on each toggle. Reconcile so that
			// re-clicking an already-selected row removes it instead of
			// duplicating it.
			const lastSelectItem = v[v.length - 1];
			const head = v.slice(0, -1);
			const indexItemExist = head.findIndex((x) => optionKey(x) === optionKey(lastSelectItem));
			const next = indexItemExist > -1 ? head.filter((_, i) => i !== indexItemExist) : v;
			onChange?.(next);
			return;
		}

		onChange?.(v);
	};

	if (!options) {
		return <>No options defined</>;
	}

	return (
		<Autocomplete<WithKey, boolean, false, false>
			size={size ?? 'medium'}
			multiple={!!multiple}
			disableCloseOnSelect={!!multiple}
			className={props.className || ''}
			fullWidth
			selectOnFocus={false}
			isOptionEqualToValue={(option, val) => optionKey(option) === optionKey(val)}
			sx={{
				borderRadius: '0.25rem',
				'& fieldset': {
					border: 'none',
					borderRadius: '0.25rem',
					fontSize: '13px',
				},
				'& .MuiAutocomplete-clearIndicator': {
					visibility: 'visible',
					color: theme.palette.primary.main,
				},
				...props.sx,
			}}
			popupIcon={<ExpandMore />}
			options={options}
			getOptionLabel={(option) => option.name}
			renderOption={(renderProps, option) =>
				!multiple ? (
					<Box
						component="li"
						sx={{
							color: theme.palette.mode === 'dark' ? 'white' : '',
							'& > .MuiSvgIcon-root': { mr: 2, flexShrink: 0 },
						}}
						{...renderProps}
					>
						{option.icon ?? ''} {option.name}
					</Box>
				) : (
					<Box
						component="li"
						sx={{
							color: option.color ?? (theme.palette.mode === 'dark' ? 'white' : ''),
							'& > .MuiSvgIcon-root': { mr: 2, flexShrink: 0 },
						}}
						{...renderProps}
					>
						<Checkbox
							icon={icon}
							checkedIcon={checkedIcon}
							style={{ marginRight: 8, color: option.color ?? '' }}
							checked={
								Array.isArray(value) ? value.some((v) => optionKey(v) === optionKey(option)) : false
							}
						/>
						{option.name}
					</Box>
				)
			}
			renderTags={(_tagValue, _getTagProps) => {
				if (multipleRenderTags) {
					return multipleRenderTags(value);
				}
				return undefined;
			}}
			onChange={(_e, v) => handleChange(v as WithKey | WithKey[] | null)}
			value={(value ?? (multiple ? [] : null)) as never}
			disabled={disabled}
			renderInput={(params) => (
				<TextField
					{...params}
					required={props.required ?? false}
					error={props.error}
					helperText={props.helperText}
					label={label}
					size={size ?? 'medium'}
					fullWidth
					onKeyPress={(e) => {
						['Enter'].includes(e.key) && e.preventDefault();
					}}
					variant="outlined"
					sx={{
						'& .MuiInputBase-input.MuiAutocomplete-input': {
							cursor: 'pointer',
						},
					}}
					InputProps={{
						...params.InputProps,
						readOnly: true,
						endAdornment: <>{params.InputProps.endAdornment}</>,
					}}
				/>
			)}
		/>
	);
};

export default CAppFilterSelect;
